import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { notificationMaster_getBellNotificationsByUserId, notificationMaster_markAsRead } from 'src/app/GlobalVariables';
import { UserLoginService } from 'src/app/Common/UserLoginService/userlogin.service';
import {
  BellNotification,
  NotificationBellListResponse,
  NotificationMarkAsReadResponseDTO,
  NotificationMasterGetBellNotificationsByUserIdRequestDTO,
  NotificationMasterMarkAsReadRequestDTO,
  mapBellApiDtoToBellNotification
} from './notification-bell.interface';

const POLL_INTERVAL_MS = 30 * 60 * 1000; // 30 min -- real-time updates now come from FCM push (see PushNotificationService), this is just the periodic reconciliation fallback
const DROPDOWN_PAGE_SIZE = 10;

/**
 * Single shared source of truth for the bell's unread list + polling, so the two bell
 * instances rendered in the DOM at once (desktop breadcrumb + mobile navbar -- see
 * home.component.html, mutually exclusive by viewport width but both always instantiated)
 * share one HTTP poll instead of each running its own. Both instances subscribe to
 * `unread$` / `unreadTotalCount$`; only the first call to startPolling() actually starts
 * the interval.
 */
@Injectable({
  providedIn: 'root'
})
export class NotificationBellService {

  private http = inject(HttpClient);
  private userLoginService = inject(UserLoginService);

  private unreadSubject = new BehaviorSubject<BellNotification[]>([]);
  unread$ = this.unreadSubject.asObservable();

  private unreadTotalCountSubject = new BehaviorSubject<number>(0);
  unreadTotalCount$ = this.unreadTotalCountSubject.asObservable();

  // Dropdown's own infinite scroll (separate from the "Show All" modal's history paging below --
  // this one appends to the shared unread$ list, since both bell instances render it).
  private unreadPageNo = 1;
  private unreadTotalCountForPaging = 0;
  private unreadLoadingSubject = new BehaviorSubject<boolean>(false);
  unreadLoading$ = this.unreadLoadingSubject.asObservable();
  // Exposed (not just internal) so the dropdown can show a "you're all caught up" footer
  // once there's nothing left to load, distinct from the big empty-state shown when the
  // list is empty from the start.
  private unreadExhaustedSubject = new BehaviorSubject<boolean>(false);
  unreadExhausted$ = this.unreadExhaustedSubject.asObservable();

  /** Fired when something outside the bell itself -- the in-app toast, today -- wants the
   *  dropdown opened (e.g. clicking an aggregated "N new notifications" toast, or an away/login
   *  summary toast, neither of which has one single destination of its own to navigate to).
   *  NotificationBellComponent subscribes and flips its own isOpen; both bell instances
   *  (desktop/mobile) do, but only the visible one matters, same pattern as the rest of this
   *  service's shared state. */
  private openDropdownSubject = new Subject<void>();
  openDropdownRequested$ = this.openDropdownSubject.asObservable();

  private pollingHandle: ReturnType<typeof setInterval> | null = null;

  private get userContext(): { userId: number; tenantId: number; tenantBranchId: number } | null {
    const user = this.userLoginService.User();
    if (!user || !user.userId || !user.companyId || !user.loggedInBranchId) { return null; }
    return { userId: user.userId, tenantId: user.companyId, tenantBranchId: user.loggedInBranchId };
  }

  /**
   * Idempotent -- safe to call from every bell instance's ngOnInit. Only the FIRST call
   * (app-wide) actually fires anything; later calls (the second bell instance mounting)
   * are no-ops -- they still get live data once the first call's refreshUnread() resolves,
   * via the shared unread$ / unreadTotalCount$ observables.
   */
  startPolling(): void {
    if (this.pollingHandle) { return; }
    this.refreshUnread();
    this.pollingHandle = setInterval(() => this.refreshUnread(), POLL_INTERVAL_MS);
  }

  stopPolling(): void {
    if (this.pollingHandle) {
      clearInterval(this.pollingHandle);
      this.pollingHandle = null;
    }
  }

  /** See openDropdownRequested$'s doc comment above. */
  requestOpenDropdown(): void {
    this.openDropdownSubject.next();
  }

  refreshUnread(): void {
    const ctx = this.userContext;
    if (!ctx) { return; }

    // A full refresh (poll tick, live push arrival, or first load) always starts over from page
    // 1 and replaces the list -- if the user had scrolled further down, this collapses that back
    // to the first page, same as the "Show All" modal would on a fresh open. loadMoreUnread()
    // below is the only thing that appends instead of replacing.
    this.unreadPageNo = 1;
    this.unreadExhaustedSubject.next(false);

    const request: NotificationMasterGetBellNotificationsByUserIdRequestDTO = {
      ...ctx,
      readStatus: 'Unread',
      pageNo: 1,
      pageSize: DROPDOWN_PAGE_SIZE
    };

    this.http.post<NotificationBellListResponse>(notificationMaster_getBellNotificationsByUserId, request).subscribe({
      next: res => {
        const items = (res.items ?? []).map(mapBellApiDtoToBellNotification);
        this.unreadSubject.next(items);
        this.unreadTotalCountForPaging = items.length > 0 ? items[0].totalCount : 0;
        this.unreadTotalCountSubject.next(this.unreadTotalCountForPaging);
        this.unreadExhaustedSubject.next(items.length < DROPDOWN_PAGE_SIZE || items.length >= this.unreadTotalCountForPaging);
      },
      error: err => console.error('[NotificationBellService] Failed to refresh unread notifications:', err)
    });
  }

  /**
   * Next page of unread notifications for the DROPDOWN's own infinite scroll -- appends to the
   * currently loaded unread$ list instead of replacing it (that's refreshUnread()'s job above).
   * Mirrors getHistoryPage()/loadNextHistoryPage() in the "Show All" modal, just against the
   * Unread-only endpoint and the shared unread$ state (since both bell instances render it,
   * unlike the modal's history list which is component-local).
   */
  loadMoreUnread(): void {
    const ctx = this.userContext;
    if (!ctx || this.unreadLoadingSubject.value || this.unreadExhaustedSubject.value) { return; }

    this.unreadLoadingSubject.next(true);
    const nextPage = this.unreadPageNo + 1;

    const request: NotificationMasterGetBellNotificationsByUserIdRequestDTO = {
      ...ctx,
      readStatus: 'Unread',
      pageNo: nextPage,
      pageSize: DROPDOWN_PAGE_SIZE
    };

    this.http.post<NotificationBellListResponse>(notificationMaster_getBellNotificationsByUserId, request).subscribe({
      next: res => {
        const items = (res.items ?? []).map(mapBellApiDtoToBellNotification);
        const merged = [...this.unreadSubject.value, ...items];
        this.unreadSubject.next(merged);
        this.unreadPageNo = nextPage;
        this.unreadTotalCountForPaging = items.length > 0 ? items[0].totalCount : this.unreadTotalCountForPaging;
        this.unreadTotalCountSubject.next(this.unreadTotalCountForPaging);
        this.unreadExhaustedSubject.next(items.length < DROPDOWN_PAGE_SIZE || merged.length >= this.unreadTotalCountForPaging);
        this.unreadLoadingSubject.next(false);
      },
      error: err => {
        console.error('[NotificationBellService] Failed to load more unread notifications:', err);
        this.unreadLoadingSubject.next(false);
      }
    });
  }

  /** One page of the full (read + unread) history, for the "Show All" modal's infinite scroll. */
  getHistoryPage(pageNo: number, pageSize: number): Observable<NotificationBellListResponse> {
    const ctx = this.userContext;
    const request: NotificationMasterGetBellNotificationsByUserIdRequestDTO = {
      ...(ctx ?? { userId: 0, tenantId: 0, tenantBranchId: 0 }),
      readStatus: null,
      pageNo,
      pageSize
    };
    return this.http.post<NotificationBellListResponse>(notificationMaster_getBellNotificationsByUserId, request);
  }

  /**
   * Marks one notification read (dismiss) or, with notificationId omitted, every unread
   * notification for this user (Clear All). Updates the shared unread state optimistically
   * -- both bell instances reflect it immediately, before the request resolves -- callers
   * should call refreshUnread() on error to resync if the request actually failed.
   */
  dismiss(notificationId: number): Observable<NotificationMarkAsReadResponseDTO> {
    this.unreadSubject.next(this.unreadSubject.value.filter(n => n.notificationId !== notificationId));
    this.unreadTotalCountSubject.next(Math.max(0, this.unreadTotalCountSubject.value - 1));
    return this.markAsRead(notificationId);
  }

  clearAll(): Observable<NotificationMarkAsReadResponseDTO> {
    this.unreadSubject.next([]);
    this.unreadTotalCountSubject.next(0);
    return this.markAsRead(null);
  }

  private markAsRead(notificationId: number | null): Observable<NotificationMarkAsReadResponseDTO> {
    const ctx = this.userContext;
    const request: NotificationMasterMarkAsReadRequestDTO = {
      ...(ctx ?? { userId: 0, tenantId: 0, tenantBranchId: 0 }),
      notificationId
    };
    return this.http.post<NotificationMarkAsReadResponseDTO>(notificationMaster_markAsRead, request);
  }
}
